// Recipe portability: encode a project's pipeline + settings into a
// copy-pasteable string and decode it back. Pure functions only — no state
// imports so this module loads in Node test environments. The function that
// mutates the project store lives in `recipe-apply.svelte.ts`.

import type {
  Pipeline,
  ProjectSettings,
  RecipePayload,
  StepType,
} from '$lib/model/types';
import { CURRENT_FORMAT_VERSION } from '$lib/model/types';
import { canonicalJson } from '$lib/util/canonicalJson';
import { sha256Hex } from '$lib/util/sha256';

const VALID_STEP_TYPES: ReadonlySet<StepType> = new Set([
  'input',
  'greyscale',
  'color_separate',
  'channel',
  'gradient_sep',
  'band',
  'halftone',
]);

export class RecipeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RecipeError';
  }
}

export interface EncodedRecipe {
  text: string;
  id: string;
}

export function buildRecipePayload(
  settings: ProjectSettings,
  pipeline: Pipeline,
  layer_order: string[],
): RecipePayload {
  return {
    format_version: CURRENT_FORMAT_VERSION,
    project_settings: settings,
    pipeline,
    layer_order,
  };
}

export async function computeRecipeId(payload: RecipePayload): Promise<string> {
  const hex = await sha256Hex(canonicalJson(payload));
  return hex.slice(0, 7);
}

export async function encodeRecipe(
  payload: RecipePayload,
): Promise<EncodedRecipe> {
  const json = canonicalJson(payload);
  const id = (await sha256Hex(json)).slice(0, 7);
  const gz = await gzipString(json);
  const text = base64FromBytes(gz);
  return { text, id };
}

export async function decodeRecipe(text: string): Promise<RecipePayload> {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new RecipeError('Recipe is empty.');
  }
  let gz: Uint8Array;
  try {
    gz = base64ToBytes(trimmed);
  } catch {
    throw new RecipeError(
      "Couldn't read recipe. Check the string is complete and try again.",
    );
  }
  let json: string;
  try {
    json = await gunzipToString(gz);
  } catch {
    throw new RecipeError(
      "Couldn't decompress recipe. The string may be truncated or corrupt.",
    );
  }
  let payload: unknown;
  try {
    payload = JSON.parse(json);
  } catch {
    throw new RecipeError('Recipe contents are not valid JSON.');
  }
  validatePayload(payload);
  return payload as RecipePayload;
}

function validatePayload(p: unknown): asserts p is RecipePayload {
  if (!isObject(p)) throw new RecipeError('Recipe payload must be an object.');
  const obj = p as Record<string, unknown>;

  if (typeof obj.format_version !== 'string') {
    throw new RecipeError('Recipe is missing format_version.');
  }
  if (obj.format_version !== CURRENT_FORMAT_VERSION) {
    if (compareVersion(obj.format_version, CURRENT_FORMAT_VERSION) > 0) {
      throw new RecipeError('Recipe was made with a newer version of silky2.');
    }
    throw new RecipeError(
      `Recipe format version ${obj.format_version} is not supported.`,
    );
  }
  if (!isObject(obj.project_settings)) {
    throw new RecipeError('Recipe is missing project_settings.');
  }
  if (!isObject(obj.pipeline)) {
    throw new RecipeError('Recipe is missing pipeline.');
  }
  if (!Array.isArray(obj.layer_order)) {
    throw new RecipeError('Recipe is missing layer_order.');
  }

  const pipeline = obj.pipeline as Record<string, unknown>;
  if (typeof pipeline.root_id !== 'string') {
    throw new RecipeError('Recipe pipeline.root_id is missing.');
  }
  if (!isObject(pipeline.steps)) {
    throw new RecipeError('Recipe pipeline.steps is missing.');
  }
  const steps = pipeline.steps as Record<string, unknown>;
  for (const [id, step] of Object.entries(steps)) {
    if (!isObject(step)) {
      throw new RecipeError(`Step "${id}" is not an object.`);
    }
    const s = step as Record<string, unknown>;
    const t = s.type;
    if (typeof t !== 'string' || !VALID_STEP_TYPES.has(t as StepType)) {
      throw new RecipeError(
        `Recipe contains an unknown step type: "${String(t)}".`,
      );
    }
    if (typeof s.id !== 'string') {
      throw new RecipeError(`Step "${id}" is missing id.`);
    }
    if (typeof s.name !== 'string') {
      throw new RecipeError(`Step "${id}" is missing name.`);
    }
    if (!isObject(s.params)) {
      throw new RecipeError(`Step "${id}" is missing params.`);
    }
    if (!Array.isArray(s.children)) {
      throw new RecipeError(`Step "${id}" is missing children.`);
    }
  }
}

function compareVersion(a: string, b: string): number {
  const pa = a.split('.').map((p) => parseInt(p, 10));
  const pb = b.split('.').map((p) => parseInt(p, 10));
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da - db;
  }
  return 0;
}

function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

// Compute the highest "s###" step-id used in a recipe so callers can bump
// the global id counter past it.
export function maxStepIdNumber(pipeline: Pipeline): number {
  let maxN = 0;
  for (const id of Object.keys(pipeline.steps)) {
    const m = /^s(\d+)$/.exec(id);
    if (m && m[1]) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n) && n > maxN) maxN = n;
    }
  }
  return maxN;
}

// ---- Base64 (browser-safe, URL-safe alphabet accepted on decode) ----

function base64FromBytes(bytes: Uint8Array): string {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(
      ...bytes.subarray(i, Math.min(i + chunk, bytes.length)),
    );
  }
  return btoa(bin);
}

function base64ToBytes(input: string): Uint8Array {
  let s = input.replace(/-/g, '+').replace(/_/g, '/').replace(/\s+/g, '');
  while (s.length % 4 !== 0) s += '=';
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---- gzip via CompressionStream / DecompressionStream ----

async function gzipString(s: string): Promise<Uint8Array> {
  const blob = new Blob([s]);
  const stream = blob.stream().pipeThrough(new CompressionStream('gzip'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

async function gunzipToString(bytes: Uint8Array): Promise<string> {
  const blob = new Blob([bytes.buffer as ArrayBuffer]);
  const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
  return await new Response(stream).text();
}
